'use client';
import React, { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Region {
  id: string;
  name: string;
  market: 'FL' | 'PR';
  cities: string[];
  households: number;
  is_active: boolean;
  sort_order: number;
}

export default function CoverageSection() {
  const [flRegions, setFlRegions] = useState<Region[]>([]);
  const [prRegions, setPrRegions] = useState<Region[]>([]);
  const [totalHouseholds, setTotalHouseholds] = useState(0);

  useEffect(() => {
    const supabase = createClient();
    const fetchRegions = async () => {
      const { data } = await supabase
        .from('regions')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (data) {
        const fl = data.filter((r: Region) => r.market === 'FL');
        const pr = data.filter((r: Region) => r.market === 'PR');
        setFlRegions(fl);
        setPrRegions(pr);
        setTotalHouseholds(data.reduce((sum: number, r: Region) => sum + (r.households || 0), 0));
      }
    };
    fetchRegions();
  }, []);

  const totalRegions = flRegions.length + prRegions.length;

  return (
    <section className="py-20">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Left: headline */}
          <div className="lg:w-80 xl:w-96 shrink-0">
            <p className="text-xs font-semibold text-brand-400 uppercase tracking-widest mb-3">Coverage Map</p>
            <h2 className="font-display text-3xl sm:text-4xl font-800 leading-tight mb-4">
              <span className="text-white">{totalRegions} Regions.</span>
              <br />
              <span className="text-gradient">{totalHouseholds.toLocaleString()}+ Homes</span>
              <br />
              <span className="text-white">Per Issue.</span>
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed mb-6">
              Each region receives 1,000 printed copies of ZipAdBook, distributed to local households and businesses every 5 weeks.
            </p>
            <div className="space-y-3">
              {[
                { label: 'Florida Regions', count: String(flRegions.length), color: 'text-brand-400' },
                { label: 'Puerto Rico Regions', count: String(prRegions.length), color: 'text-accent-blue' },
                { label: 'Total Households Reached', count: `${totalHouseholds.toLocaleString()}+`, color: 'text-accent-purple' },
              ]?.map((s) => (
                <div key={`coverage-stat-${s?.label}`} className="flex items-center justify-between glass-card rounded-xl px-4 py-3">
                  <span className="text-sm text-slate-400">{s?.label}</span>
                  <span className={`font-display font-bold text-lg ${s?.color} tabular-nums`}>{s?.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: region grid */}
          <div className="flex-1">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-brand-500" />
                <span className="text-sm text-slate-400 font-medium">Florida</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-accent-blue" />
                <span className="text-sm text-slate-400 font-medium">Puerto Rico</span>
              </div>
            </div>

            {/* Florida regions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-3">
              {flRegions?.map((region) => (
                <div key={`region-fl-${region?.id}`} className="glass-card rounded-xl p-4 border-l-2 border-brand-500">
                  <div className="flex items-start gap-2 mb-2">
                    <MapPin size={14} className="text-brand-400 mt-0.5 shrink-0" />
                    <p className="text-sm font-semibold text-white leading-tight">{region?.name}</p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {region?.cities?.map((city) => (
                      <span key={`city-fl-${region?.id}-${city}`} className="chip" style={{ fontSize: '10px' }}>{city}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* PR regions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {prRegions?.map((region) => (
                <div key={`region-pr-${region?.id}`} className="glass-card rounded-xl p-4 border-l-2 border-sky-500">
                  <div className="flex items-start gap-2 mb-2">
                    <MapPin size={14} className="text-sky-400 mt-0.5 shrink-0" />
                    <p className="text-sm font-semibold text-white leading-tight">{region?.name}</p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {region?.cities?.map((city) => (
                      <span key={`city-pr-${region?.id}-${city}`} className="chip" style={{ fontSize: '10px', borderColor: 'rgba(56,189,248,0.3)', color: '#7dd3fc' }}>{city}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}